import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import AdminShell, { Body, Section, Stat } from "@/components/admin/AdminShell";
import { CostSettings } from "@/components/admin/CostSettings";
import { Card } from "@/components/Common";
import { enc } from "@/lib/api";
import { label, num, useAdminGet, usd } from "@/lib/admin";
import { cn } from "@/lib/utils";

const contrib = (r) => r.gross_contribution ?? (r.revenue || 0) - (r.ai_cost || 0) - (r.payment_fees || 0) - (r.infra_cost || 0);
const margin = (r) => r.margin_pct ?? (r.revenue ? (100 * contrib(r)) / r.revenue : null);
const tone = (v) => (v == null ? "" : v < 0 ? "text-red-400" : "text-emerald-300");

const ROWS = [
  ["Users", (r) => num(r.users)], ["Subscription price", (r) => (r.price != null ? `${usd(r.price)}/mo` : "—")], ["Revenue", (r) => usd(r.revenue)],
  ["AI cost (actual usage)", (r) => usd(r.ai_cost)], ["Payment fees (actual)", (r) => usd(r.payment_fees)], ["Infrastructure (allocated)", (r) => usd(r.infra_cost)],
  ["Gross contribution", (r) => <span className={tone(contrib(r))}>{usd(contrib(r))}</span>], ["Margin", (r) => <span className={tone(margin(r))}>{margin(r) == null ? "—" : `${Math.round(margin(r))}%`}</span>],
  ["Per user: AI cost", (r) => usd(r.per_user?.ai_cost)], ["Per user: contribution", (r) => <span className={tone(r.per_user?.contribution)}>{usd(r.per_user?.contribution)}</span>],
];

const TierTable = ({ tiers }) => (
  <Card className="overflow-x-auto p-0" data-testid="profit-table">
    <table className="w-full min-w-[520px] text-sm">
      <thead><tr className="border-b border-[#27272A] text-left text-[11px] uppercase tracking-wider text-zinc-500"><th className="p-3">Metric</th>{tiers.map((t) => <th key={t.tier} className="p-3 text-right text-[#F3C649]">{label(t.tier)}</th>)}</tr></thead>
      <tbody>{ROWS.map(([k, f]) => (
        <tr key={k} className={cn("border-b border-[#27272A]/60 last:border-0", k === "Gross contribution" && "bg-[#33290D]/30 font-semibold")}>
          <td className="p-3 text-zinc-400">{k}</td>{tiers.map((t) => <td key={t.tier} className="p-3 text-right font-mono text-zinc-200" data-testid={`profit-${t.tier}-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{f(t)}</td>)}
        </tr>
      ))}</tbody>
    </table>
  </Card>
);

const List = ({ rows, cols, testid }) => (
  <Card className="space-y-1" data-testid={testid}>
    {rows.length ? rows.map(cols) : <p className="text-xs text-zinc-500">No data yet.</p>}
  </Card>
);

export default function Profitability() {
  const [days, setDays] = useState(30);
  const q = useAdminGet(`/admin/cc/profitability?days=${days}`);
  const settings = useAdminGet("/admin/cc/cost-settings");
  const actions = [7, 30, 90].map((n) => (
    <button key={n} onClick={() => setDays(n)} data-testid={`profit-days-${n}`} className={cn("rounded-full px-3 py-1 text-xs", days === n ? "bg-[#D4AF37] font-semibold text-black" : "border border-[#27272A] text-zinc-400 hover:text-zinc-100")}>{n} days</button>
  ));
  return (
    <AdminShell title="Profitability" subtitle="Optimise for profitable subscribers, not just subscriber count." actions={actions}>
      <Body q={q}>{(d) => {
        const t = d.totals || {}, budget = d.ai_budget || {};
        const free = (d.tiers || []).find((x) => x.tier === "free");
        return (
          <>
            {budget.used_pct >= (settings.data?.alert_at_pct ?? 80) && <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="profit-budget-alert"><AlertTriangle className="h-4 w-4" />AI spend is at {Math.round(budget.used_pct)}% of this month's budget.</div>}
            <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Stat k="Revenue" v={usd(t.revenue)} testid="profit-total-revenue" /><Stat k="AI cost" v={usd(t.ai_cost)} sub={budget.month_to_date != null ? `${usd(budget.month_to_date)} of ${usd(budget.budget)} this month` : null} testid="profit-total-ai" />
              <Stat k="Payment fees" v={usd(t.payment_fees)} /><Stat k="Gross contribution" v={usd(contrib(t))} tone={tone(contrib(t))} testid="profit-total-contribution" />
              <Stat k="Free users' AI cost" v={usd(free?.ai_cost)} sub={free ? `${usd(free.per_user?.ai_cost)} per free user` : null} tone="text-amber-300" testid="profit-free-ai" />
            </div>
            <Section title="Contribution by plan"><TierTable tiers={d.tiers || []} /></Section>
            <div className="mb-8 grid gap-6 lg:grid-cols-2">
              <Section title="AI cost by feature" className="mb-0"><List testid="profit-features" rows={d.ai_cost_by_feature || []} cols={(f) => <div key={f.feature} className="flex justify-between text-xs"><span className="text-zinc-300">{label(f.feature)}</span><span className="font-mono text-zinc-400">{num(f.calls)} calls · {usd(f.cost)}</span></div>} /></Section>
              <Section title="Heaviest AI users" className="mb-0"><List testid="profit-top-users" rows={d.top_ai_users || []} cols={(u) => <Link key={u.key} to={`/admin/cc/prospects/${enc(u.key)}`} className="flex justify-between rounded px-1 py-0.5 text-xs hover:bg-[#121214]"><span className="truncate text-zinc-200">{u.name || u.email} <span className="text-zinc-500">· {u.tier}</span></span><span className="font-mono text-zinc-400">{usd(u.ai_cost)}</span></Link>} /></Section>
            </div>
          </>
        );
      }}</Body>
      <Section title="Costs & safeguards" testid="profit-settings"><CostSettings initial={settings.data} onSaved={(s) => { settings.setData(s); q.reload(); }} /></Section>
    </AdminShell>
  );
}
